import { useState, useEffect } from 'react';
import { employeeAPI, lettersReportsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { FileText, Download, Search, Loader2, Users, Receipt, Clock, FileSpreadsheet, TrendingDown, AlertTriangle, Building2, BarChart3, CalendarDays, History, Shield, BookOpen } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const LETTERS = [
  { key: 'address-proof', label: 'Address Proof', description: 'Certifies residential address on record', icon: FileText },
  { key: 'confirmation', label: 'Confirmation Letter', description: 'Full-time confirmation with effective date', icon: FileText, needsDate: true, dateLabel: 'Confirmation Effective Date' },
  { key: 'employment', label: 'Employment Letter', description: 'Certifies current employment and designation', icon: FileText },
  { key: 'increment', label: 'Increment Letter', description: 'Salary increment with CTC annexure', icon: FileText },
  { key: 'relieving', label: 'Relieving Letter', description: 'Formal separation confirmation', icon: FileText, needsDate: true, dateLabel: 'Last Working Day' },
];

function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const LettersReports = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState({});

  const [dateDialog, setDateDialog] = useState({ open: false, employee: null, letterKey: '', dateLabel: '' });
  const [dialogDate, setDialogDate] = useState('');

  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportDownloading, setReportDownloading] = useState({});

  useEffect(() => {
    employeeAPI.getEmployees({ limit: 1000 })
      .then(res => setEmployees(res.data.employees || res.data || []))
      .catch(() => toast.error('Failed to load employees'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.employee_code || '').toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q) ||
      (e.designation || '').toLowerCase().includes(q)
    );
  });

  const handleDownloadLetter = async (letterKey, employee) => {
    const letter = LETTERS.find(l => l.key === letterKey);
    if (letter?.needsDate) {
      setDialogDate('');
      setDateDialog({ open: true, employee, letterKey, dateLabel: letter.dateLabel });
      return;
    }
    await doDownloadLetter(letterKey, employee);
  };

  const doDownloadLetter = async (letterKey, employee, extraDate = null) => {
    const dlKey = `${letterKey}-${employee.id}`;
    setDownloading(prev => ({ ...prev, [dlKey]: true }));
    try {
      let res;
      if (letterKey === 'address-proof') res = await lettersReportsAPI.downloadAddressProofLetter(employee.id);
      else if (letterKey === 'confirmation') res = await lettersReportsAPI.downloadConfirmationLetter(employee.id, extraDate);
      else if (letterKey === 'employment') res = await lettersReportsAPI.downloadEmploymentLetter(employee.id);
      else if (letterKey === 'increment') res = await lettersReportsAPI.downloadIncrementLetter(employee.id);
      else if (letterKey === 'relieving') res = await lettersReportsAPI.downloadRelievingLetter(employee.id, extraDate);
      const label = LETTERS.find(l => l.key === letterKey)?.label || letterKey;
      triggerDownload(res.data, `${label.replace(/ /g, '_')}_${employee.first_name}_${employee.last_name}.pdf`);
      toast.success(`${label} downloaded`);
    } catch (err) {
      const msg = err?.response?.data ? await err.response.data.text?.() : null;
      toast.error(msg ? JSON.parse(msg)?.detail || 'Download failed' : 'Download failed');
    } finally {
      setDownloading(prev => ({ ...prev, [dlKey]: false }));
    }
  };

  const handleDateDialogConfirm = async () => {
    if (!dialogDate) {
      toast.error(`Please select a date`);
      return;
    }
    const { employee, letterKey } = dateDialog;
    setDateDialog({ open: false, employee: null, letterKey: '', dateLabel: '' });
    await doDownloadLetter(letterKey, employee, dialogDate);
  };

  const handleDownloadReport = async (reportKey) => {
    setReportDownloading(prev => ({ ...prev, [reportKey]: true }));
    try {
      const ml = MONTHS.find(m => m.value === reportMonth)?.label;
      const today = new Date().toISOString().slice(0, 10);
      const actions = {
        'attendance':       [() => lettersReportsAPI.downloadAttendanceReport(reportMonth, reportYear),      `Attendance_Report_${ml}_${reportYear}.xlsx`],
        'joining-exit':     [() => lettersReportsAPI.downloadJoiningExitReport(),                            `Joining_Exit_Report_${today}.xlsx`],
        'leave':            [() => lettersReportsAPI.downloadLeaveReport(reportMonth, reportYear),           `Leave_Report_${ml}_${reportYear}.xlsx`],
        'payroll':          [() => lettersReportsAPI.downloadPayrollReport(reportMonth, reportYear),         `Payroll_Report_${ml}_${reportYear}.xlsx`],
        'pf-esi':           [() => lettersReportsAPI.downloadPfEsiReport(reportMonth, reportYear),           `PF_ESI_Report_${ml}_${reportYear}.xlsx`],
        'tds':              [() => lettersReportsAPI.downloadTdsReport(reportMonth, reportYear),             `TDS_Report_${ml}_${reportYear}.xlsx`],
        'dept-payroll-cost':[() => lettersReportsAPI.downloadDeptPayrollCostReport(reportMonth, reportYear), `Dept_Payroll_Cost_${ml}_${reportYear}.xlsx`],
        'salary-structure': [() => lettersReportsAPI.downloadSalaryStructureReport(),                       `Salary_Structure_Report_${today}.xlsx`],
        'late-arrivals':    [() => lettersReportsAPI.downloadLateArrivalsReport(reportMonth, reportYear),    `Late_Arrivals_${ml}_${reportYear}.xlsx`],
        'absenteeism':      [() => lettersReportsAPI.downloadAbsenteeismReport(reportMonth, reportYear),     `Absenteeism_Report_${ml}_${reportYear}.xlsx`],
        'leave-balance':    [() => lettersReportsAPI.downloadLeaveBalanceReport(reportYear),                 `Leave_Balance_${reportYear}.xlsx`],
        'leave-history':    [() => lettersReportsAPI.downloadLeaveHistoryReport(reportMonth, reportYear),    `Leave_History_${ml}_${reportYear}.xlsx`],
        'headcount':        [() => lettersReportsAPI.downloadHeadcountReport(),                              `Headcount_Report_${today}.xlsx`],
      };
      const [fn, filename] = actions[reportKey] || [];
      if (!fn) return;
      const res = await fn();
      triggerDownload(res.data, filename);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error(err?.response?.status === 404 ? 'No data found for the selected period' : 'Failed to download report');
    } finally {
      setReportDownloading(prev => ({ ...prev, [reportKey]: false }));
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Letters & Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Generate HR letters for employees and download data reports</p>
      </div>

      <Tabs defaultValue="letters">
        <TabsList>
          <TabsTrigger value="letters" className="gap-2"><FileText className="h-4 w-4" />Letters</TabsTrigger>
          <TabsTrigger value="reports" className="gap-2"><FileSpreadsheet className="h-4 w-4" />Reports</TabsTrigger>
        </TabsList>

        {/* ── LETTERS TAB ── */}
        <TabsContent value="letters" className="mt-4 space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search employees..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="rounded-xl border bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Status</TableHead>
                    {LETTERS.map(l => (
                      <TableHead key={l.key} className="text-center whitespace-nowrap">{l.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4 + LETTERS.length} className="text-center text-slate-400 py-12">
                        No employees found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map(emp => (
                      <TableRow key={emp.id}>
                        <TableCell>
                          <div className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</div>
                          <div className="text-xs text-slate-400">{emp.employee_code}</div>
                        </TableCell>
                        <TableCell className="text-slate-600">{emp.department || '—'}</TableCell>
                        <TableCell className="text-slate-600">{emp.designation || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={emp.status === 'ACTIVE' ? 'default' : 'secondary'} className="text-xs">
                            {emp.status}
                          </Badge>
                        </TableCell>
                        {LETTERS.map(letter => {
                          const dlKey = `${letter.key}-${emp.id}`;
                          const isLoading = downloading[dlKey];
                          return (
                            <TableCell key={letter.key} className="text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                disabled={isLoading}
                                onClick={() => handleDownloadLetter(letter.key, emp)}
                                title={`Download ${letter.label}`}
                              >
                                {isLoading
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Download className="h-3.5 w-3.5" />
                                }
                              </Button>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── REPORTS TAB ── */}
        <TabsContent value="reports" className="mt-4">
          <div className="mb-5 flex items-center gap-3">
            <Select value={String(reportMonth)} onValueChange={v => setReportMonth(Number(v))}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(reportYear)} onValueChange={v => setReportYear(Number(v))}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">Month/year used for period-specific reports · Year used for Leave Balance</p>
          </div>

          {/* ── Payroll ── */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Payroll</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { key: 'payroll',           icon: Receipt,    color: 'green',  title: 'Payroll Report',            desc: 'Earnings, deductions, and net pay per employee' },
              { key: 'pf-esi',            icon: Shield,     color: 'blue',   title: 'PF & ESI Statutory',        desc: 'Employee & employer PF/ESI contributions with UAN' },
              { key: 'tds',               icon: BookOpen,   color: 'violet', title: 'TDS Report',                desc: 'Monthly TDS with YTD cumulative per employee' },
              { key: 'dept-payroll-cost', icon: Building2,  color: 'slate',  title: 'Dept Payroll Cost',         desc: 'Gross, employer contributions, and CTC cost by department' },
            ].map(({ key, icon: Icon, color, title, desc }) => (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                      <Icon className={`h-4 w-4 text-${color}-600`} />
                    </div>
                    <CardTitle className="text-sm">{title}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">{desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full gap-2" disabled={reportDownloading[key]} onClick={() => handleDownloadReport(key)}>
                    {reportDownloading[key] ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating...</> : <><Download className="h-3.5 w-3.5" />Download</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Attendance ── */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Attendance</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {[
              { key: 'attendance',    icon: Clock,          color: 'amber',  title: 'Attendance Report',    desc: 'Day-by-day status with LOP, week-off, and work hours' },
              { key: 'late-arrivals', icon: AlertTriangle,  color: 'orange', title: 'Late Arrivals',        desc: 'All late check-ins with time and minutes late' },
              { key: 'absenteeism',   icon: TrendingDown,   color: 'red',    title: 'Absenteeism Report',   desc: 'Absence count and absence % per employee' },
            ].map(({ key, icon: Icon, color, title, desc }) => (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                      <Icon className={`h-4 w-4 text-${color}-600`} />
                    </div>
                    <CardTitle className="text-sm">{title}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">{desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full gap-2" disabled={reportDownloading[key]} onClick={() => handleDownloadReport(key)}>
                    {reportDownloading[key] ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating...</> : <><Download className="h-3.5 w-3.5" />Download</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Leave ── */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Leave</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {[
              { key: 'leave',         icon: FileSpreadsheet, color: 'purple', title: 'Leave Report',          desc: 'Opening, earned, availed, and closing balance by leave type' },
              { key: 'leave-balance', icon: BarChart3,       color: 'teal',   title: 'Leave Balance Summary', desc: 'Annual leave balances across all leave types (uses year only)' },
              { key: 'leave-history', icon: History,         color: 'indigo', title: 'Leave History',         desc: 'All leave requests with dates, days, reason, and status' },
            ].map(({ key, icon: Icon, color, title, desc }) => (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                      <Icon className={`h-4 w-4 text-${color}-600`} />
                    </div>
                    <CardTitle className="text-sm">{title}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">{desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full gap-2" disabled={reportDownloading[key]} onClick={() => handleDownloadReport(key)}>
                    {reportDownloading[key] ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating...</> : <><Download className="h-3.5 w-3.5" />Download</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Employee ── */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Employee</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { key: 'joining-exit',     icon: Users,           color: 'blue',  title: 'Joining & Exit',        desc: 'All employees with joining dates, status, and last working day' },
              { key: 'salary-structure', icon: Receipt,         color: 'green', title: 'Salary Structure',      desc: 'CTC and component-wise monthly breakdown for active employees' },
              { key: 'headcount',        icon: CalendarDays,    color: 'cyan',  title: 'Headcount Summary',     desc: 'Dept, gender, employment type, and location breakdown with full list' },
            ].map(({ key, icon: Icon, color, title, desc }) => (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                      <Icon className={`h-4 w-4 text-${color}-600`} />
                    </div>
                    <CardTitle className="text-sm">{title}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">{desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button size="sm" className="w-full gap-2" disabled={reportDownloading[key]} onClick={() => handleDownloadReport(key)}>
                    {reportDownloading[key] ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating...</> : <><Download className="h-3.5 w-3.5" />Download</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Date picker dialog (used for confirmation & relieving letters) */}
      <Dialog open={dateDialog.open} onOpenChange={open => setDateDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{dateDialog.dateLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="letter-date">
              {dateDialog.dateLabel} for{' '}
              <span className="font-semibold">
                {dateDialog.employee?.first_name} {dateDialog.employee?.last_name}
              </span>
            </Label>
            <Input
              id="letter-date"
              type="date"
              value={dialogDate}
              onChange={e => setDialogDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateDialog({ open: false, employee: null, letterKey: '', dateLabel: '' })}>
              Cancel
            </Button>
            <Button onClick={handleDateDialogConfirm} disabled={!dialogDate}>
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LettersReports;
